import { DT } from "../../constants.ts";
import { add, flatBasis, norm, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyHeal, applyStatus, explode, isEnemy, moveProjectile, playersInRadius, spawnProjectile, spawnZone } from "../../sim/combat.ts";
import { nearestEnemy, spawnConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, pk, zk, type HeroModule } from "../module.ts";
import { action, charge, commonInput, constructProjectile, consume, contactEnemies, glide, groundAimPoint, launchProjectile, ownerAlive, ready, secs, simpleProjectile, toGround, trigger, useUlt } from "../kitlib.ts";

export const V = { webbed: 0, nestNet: 1, ult: 2, sweepHitTick: 3 } as const;
export const PK = { Cluster: pk(HeroId.PennyParka, 0), Snare: pk(HeroId.PennyParka, 1), Drone: pk(HeroId.PennyParka, 2) } as const;
export const ZK = { Web: zk(HeroId.PennyParka, 0) } as const;
export const CK = { Nest: ck(HeroId.PennyParka, 0), Mine: ck(HeroId.PennyParka, 1) } as const;

const def: HeroDef = {
  id: HeroId.PennyParka,
  slug: "penny-parka",
  name: "Penny Parka",
  role: Role.Tank,
  color: "#e2556d",
  blurb: "Teen pilot in a spider mech who locks down ground with webs, mines, a drone nest and sweeping mech rushes.",
  health: 650,
  armor: 0,
  shields: 0,
  radius: 0.72,
  height: 2.25,
  headRadius: 0.24,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Web Salvo", key: "LMB", description: "Fire rapid mech web bolts.", cooldown: 0 },
    [Slot.Secondary]: { name: "Tangle Shot", key: "RMB", description: "Fire a snare that roots an enemy or makes a cyber-web on terrain.", cooldown: 3, charges: 2 },
    [Slot.Ability1]: { name: "Spider Hub", key: "Shift", description: "Aim, then LMB to deploy a drone nest that projects webs and launches spider drones.", cooldown: 15 },
    [Slot.Ability2]: { name: "Crawler Mine", key: "E", description: "Launch a hidden mine that arms on the ground and explodes near enemies.", cooldown: 4, charges: 4 },
    [Slot.Ability3]: { name: "Thread Zip", key: "F", description: "Zip toward your nest or dash along your webs.", cooldown: 3 },
    [Slot.Ultimate]: { name: "Sweep Mode", key: "Q", description: "Overdrive the mech, rushing through foes while dropping webs and mines.", cooldown: 0 },
  },
  passive: { name: "Home Web", description: "Penny heals while standing in her webs or near her nest." },
};

function webAt(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, pos = sim.posOf(owner), radius = 4, seconds = 6) {
  return spawnZone(sim, owner, ZK.Web, pos, radius, seconds);
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      let webbed = 0;
      for (const z of sim.zoneEids()) {
        if (c.Zone.kind[z] === ZK.Web && c.Zone.team[z] === c.Player.team[eid]) {
          const p = sim.posOf(z);
          if (Math.hypot(c.Pos.x[eid]! - p.x, c.Pos.z[eid]! - p.z) <= c.Zone.radius[z]!) webbed = 1;
        }
      }
      setHv(c, eid, V.webbed, webbed);
      if (hv(c, eid, V.ult) > 0) {
        setHv(c, eid, V.ult, hv(c, eid, V.ult) - 1);
        if (sim.tick % 30 === 0) webAt(sim, eid, sim.posOf(eid), 4, 4);
        if (sim.tick % 42 === 0) spawnConstruct(sim, eid, CK.Mine, toGround(sim, add(sim.posOf(eid), { x: 0, y: 1, z: 0 })), input.yaw, { seconds: 10 });
        contactEnemies(sim, eid, 5, (t) => {
          if (hv(c, eid, V.sweepHitTick) === sim.tick) return;
          setHv(c, eid, V.sweepHitTick, sim.tick);
          applyDamage(sim, t, 60, eid);
          applyStatus(sim, t, "root", 0.8, { source: eid });
        });
      }
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        setHv(c, eid, V.ult, secs(12));
        action(sim, eid, ActionKind.Ult, 35);
        applyStatus(sim, eid, "unstoppable", 12, { source: eid });
        applyStatus(sim, eid, "haste", 12, { hastePct: 0.7, source: eid });
      }

      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        action(sim, eid, ActionKind.Cast, 8);
        charge(sim, eid, input, { speed: 30, seconds: 1 });
      }

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 12);
        const p = groundAimPoint(sim, eid, 28, 0.45);
        spawnConstruct(sim, eid, CK.Mine, p, input.yaw, { seconds: 14 });
      }

      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 18);
        const p = targetPoint(sim, eid, Slot.Ability1);
        const nest = spawnConstruct(sim, eid, CK.Nest, p.pos, p.yaw, { seconds: 18 });
        if (nest !== undefined) setHv(c, eid, V.nestNet, c.Net.id[nest]!);
      }

      if ((pressed & Btn.Secondary) && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 10);
        launchProjectile(sim, eid, PK.Snare, 60, input, { radius: 0.18, seconds: 1.2 });
      }

      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = 15;
        action(sim, eid, ActionKind.Fire, 8);
        launchProjectile(sim, eid, PK.Cluster, 150, input, { radius: 0.12, seconds: 0.75 });
      }
    },
    movement(sim, eid, input, mods) {
      if (input.buttons & Btn.Jump) glide(sim, eid, input, mods, { fallSpeed: 4, driftSpeed: 0.6 });
    },
  },
  targets: {
    [Slot.Ability1]: { range: 10, clearance: 0.95, construct: CK.Nest, verb: "Place" },
  },
  projectiles: {
    [PK.Cluster]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) applyDamage(sim, hit.target, 30, owner, { crit: hit.head, critMul: 1.5, pos: hit.pos });
        else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
      },
      { color: "#ff82ad", size: 0.13, trail: true },
    ),
    [PK.Snare]: {
      visual: { color: "#ffffff", size: 0.17, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (hit) {
          if (ownerAlive(sim, owner) && hit.target !== undefined) {
            applyDamage(sim, hit.target, 5, owner, { pos: hit.pos });
            applyStatus(sim, hit.target, "root", 0.7, { source: owner });
            applyStatus(sim, hit.target, "slow", 1.0, { slowPct: 0.3, source: owner });
            webAt(sim, owner, hit.pos, 3, 4);
          } else if (ownerAlive(sim, owner)) webAt(sim, owner, hit.pos, 3, 5);
          sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Hook });
          sim.destroyEntity(p);
        } else if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
    [PK.Drone]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) applyDamage(sim, hit.target, 40, owner, { pos: hit.pos });
        else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
      },
      { color: "#f4ff4f", size: 0.16, trail: true },
    ),
  },
  zones: {
    [ZK.Web]: {
      visual: { color: "#ff9bc4", dome: false },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        if (!ownerAlive(sim, owner)) return;
        for (const { eid: t } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => !isEnemy(sim, owner, e), false)) {
          applyHeal(sim, owner, t, (t === owner ? 25 : 15) * DT, { noUlt: true, silent: true });
          applyStatus(sim, t, "haste", 0.25, { source: owner, hastePct: 0.25 });
        }
        for (const { eid: t } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => isEnemy(sim, owner, e), false)) applyStatus(sim, t, "slow", 0.25, { slowPct: 0.25, source: owner });
      },
    },
  },
  constructs: {
    [CK.Nest]: {
      maxHp: 350,
      seconds: 18,
      shape: { kind: "cyl", r: 0.9, h: 1.1 },
      solid: true,
      blocksShots: true,
      damageable: true,
      heals: true,
      visual: { color: "#ff5e93", shape: "cyl" },
      tick(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (!ownerAlive(sim, owner)) return;
        webAt(sim, owner, sim.posOf(e), 12, 0.2);
        if (sim.tick % 90 === 0) {
          const target = nearestEnemy(sim, owner, add(sim.posOf(e), { x: 0, y: 0.7, z: 0 }), 18, { los: true });
          if (target) {
            const from = add(sim.posOf(e), { x: 0, y: 0.8, z: 0 });
            const to = target.construct ? sim.posOf(target.eid) : add(sim.posOf(target.eid), { x: 0, y: sim.hero(target.eid).height * 0.5, z: 0 });
            constructProjectile(sim, e, PK.Drone, 34, norm({ x: to.x - from.x, y: to.y - from.y, z: to.z - from.z }), 1.6, { radius: 0.12, height: 0.8 });
          }
        }
      },
    },
    [CK.Mine]: {
      maxHp: 60,
      seconds: 14,
      shape: { kind: "cyl", r: 0.42, h: 0.22 },
      solid: false,
      blocksShots: false,
      damageable: true,
      visual: { color: "#f4ff4f", shape: "cyl" },
      tick(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (!ownerAlive(sim, owner)) return;
        const hit = playersInRadius(sim, add(sim.posOf(e), { x: 0, y: 0.3, z: 0 }), 2.4, (x) => isEnemy(sim, owner, x), false)[0];
        if (hit) {
          explode(sim, owner, sim.posOf(e), { radius: 3, damage: 100, minDamage: 40, fxKind: FxKind.Grenade });
          sim.destroyEntity(e);
        }
      },
    },
  },
};

void flatBasis;
void scale;
void addOverhealth;
void spawnProjectile;
void trigger;
export default module;
